// Layout/PhoneStatus — the browser phone's state, in the top bar, from the
// events services/voiceBootstrap emits. One place for it: this replaces the
// floating "Reconnecting…" pill and the failure banner.
//   starting      no event yet (the device boots silently after login)
//   ready         registered / reconnected
//   reconnecting  re-auth in progress — the user does nothing
//   offline       three straight reconnect failures, or the device errored
//   off           nothing registered within 20 s — this login has no phone
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Phone, PhoneOff, RefreshCw } from 'lucide-react';
import { reconnectVoice } from '../../services/voiceBootstrap';
import Menu, { MenuItem, MenuLabel } from '../ui/Menu';

const COPY = {
  starting: { label: 'Phone starting…', tone: 'muted' },
  ready: { label: 'Phone ready', tone: 'ok' },
  reconnecting: { label: 'Reconnecting…', tone: 'warn' },
  offline: { label: 'Phone offline', tone: 'danger' },
  off: { label: 'Phone off', tone: 'muted' },
};

export function usePhoneState() {
  const [state, setState] = useState('starting');
  useEffect(() => {
    const ready = () => setState('ready');
    const reconnecting = () => setState('reconnecting');
    const failed = () => setState('offline');
    const on = (name, fn) => window.addEventListener(name, fn);
    const off = (name, fn) => window.removeEventListener(name, fn);
    on('voice-registered', ready); on('voice-reconnected', ready);
    on('voice-reconnecting', reconnecting);
    on('voice-reconnect-failed', failed); on('voice-error', failed);
    const t = setTimeout(() => setState((s) => (s === 'starting' ? 'off' : s)), 20000);
    return () => {
      off('voice-registered', ready); off('voice-reconnected', ready);
      off('voice-reconnecting', reconnecting);
      off('voice-reconnect-failed', failed); off('voice-error', failed);
      clearTimeout(t);
    };
  }, []);
  return [state, setState];
}

export default function PhoneStatus({ compact = false }) {
  const navigate = useNavigate();
  const [state, setState] = usePhoneState();
  const copy = COPY[state] || COPY.off;
  const retry = async () => {
    setState('reconnecting');
    const ok = await reconnectVoice();
    setState(ok ? 'ready' : 'offline');
  };
  const Icon = state === 'reconnecting' ? Loader2 : state === 'offline' || state === 'off' ? PhoneOff : Phone;
  return (
    <Menu
      className="shell-phone"
      trigger={(
        <button type="button" className={`shell-chip shell-phone__btn tone-${copy.tone} ${compact ? 'is-compact' : ''}`} aria-label={copy.label} title={copy.label}>
          <Icon size={15} className={state === 'reconnecting' ? 'shell-spin' : ''} aria-hidden="true" />
          <span className="shell-phone__dot" aria-hidden="true" />
          {compact ? null : <span className="shell-chip__text">{copy.label}</span>}
        </button>
      )}
    >
      <MenuLabel>{copy.label}</MenuLabel>
      {state === 'offline' || state === 'off' ? (
        <MenuItem icon={<RefreshCw size={14} />} onSelect={retry}>Try to connect</MenuItem>
      ) : null}
      <MenuItem icon={<Phone size={14} />} onSelect={() => navigate('/communication/dialer')}>Open dialer</MenuItem>
      <MenuItem onSelect={() => navigate('/communication/calls')}>Calls</MenuItem>
    </Menu>
  );
}
